/**
 * ConsistencyStrip — ported from the OnFlow Design System project
 * (v2/components/track/ConsistencyStrip.jsx).
 *
 * A bar per day. This is the component that catches a run of cheat days: three
 * amber-or-red bars in a row is instantly visible in a way a weekly average
 * never is. Bar height encodes magnitude, colour encodes verdict.
 */
import type { CSSProperties } from 'react';

export type ConsistencyTone = 'hit' | 'under' | 'over' | 'rest' | 'none';

const TONE: Record<ConsistencyTone, string> = {
  hit: 'var(--on-track)',
  under: 'var(--drifting)',
  over: 'var(--off-track)',
  rest: 'var(--ink-3)',
  none: 'var(--paper-3)',
};

export interface ConsistencyDay {
  label: string;
  /** Percent of target, 0-100+. Clamped to 8-100 for bar height. */
  value: number;
  tone: ConsistencyTone;
  /** Appended to the bar's title attribute. */
  detail?: string;
  today?: boolean;
}

interface ConsistencyStripProps {
  days: ConsistencyDay[];
  height?: number;
  showAxis?: boolean;
  onSelect?: (index: number) => void;
  style?: CSSProperties;
}

export function ConsistencyStrip({
  days,
  height = 56,
  showAxis = true,
  onSelect,
  style,
}: ConsistencyStripProps) {
  return (
    <div style={style}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height }}>
        {days.map((d, i) => {
          const pct = Math.max(8, Math.min(100, d.value));
          const c = TONE[d.tone] ?? TONE.none;
          const empty = d.tone === 'none';
          return (
            <button
              key={d.label + String(i)}
              type="button"
              onClick={onSelect ? () => onSelect(i) : undefined}
              title={d.label + (d.detail ? ` · ${d.detail}` : '')}
              style={{
                flex: 1,
                minWidth: 0,
                height: '100%',
                display: 'flex',
                alignItems: 'flex-end',
                background: 'transparent',
                border: 0,
                padding: 0,
                cursor: onSelect ? 'pointer' : 'default',
              }}
            >
              <span
                style={{
                  display: 'block',
                  width: '100%',
                  height: empty ? '100%' : `${pct}%`,
                  borderRadius: 'var(--r-xs)',
                  background: empty
                    ? 'var(--paper-2)'
                    : `linear-gradient(180deg, ${c} 0%, color-mix(in srgb, ${c} 55%, transparent) 100%)`,
                  border: empty ? '1px dashed var(--edge)' : 'none',
                  boxSizing: 'border-box',
                  transition: 'height var(--t-arc) var(--ease-out)',
                }}
              />
            </button>
          );
        })}
      </div>
      {showAxis && (
        <div style={{ display: 'flex', gap: 4, marginTop: 7 }}>
          {days.map((d, i) => (
            <span
              key={d.label + String(i)}
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: 'center',
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: d.today ? 'var(--ink-1)' : 'var(--ink-3)',
                fontWeight: d.today ? 600 : 400,
              }}
            >
              {d.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Compact legend for the strip's four tones. */
export function ConsistencyLegend({ style }: { style?: CSSProperties }) {
  const keys: [ConsistencyTone, string][] = [
    ['hit', 'In range'],
    ['under', 'Under'],
    ['over', 'Over'],
    ['none', 'Not logged'],
  ];
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', ...style }}>
      {keys.map(([k, t]) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{ width: 8, height: 8, borderRadius: 3, background: TONE[k], flexShrink: 0 }}
          />
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}
          >
            {t}
          </span>
        </span>
      ))}
    </div>
  );
}
